import { Link } from "react-router-dom";
import { useState } from "react";
import { Instagram, Music2, Mail, Send } from "lucide-react";
import { subscribeEmail } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import { useLiveAnnounce, LiveRegion } from "@/lib/a11y";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const { message: liveMessage, announce } = useLiveAnnounce();

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await subscribeEmail({ email, source: "footer", consent: true });
            toast.success("Thank you — you're on the journey.");
            announce("Thank you. You are now on the journey.");
            setEmail("");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
            toast.error(msg);
            announce(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer data-testid={TID.footer.wrapper} className="bg-charcoal text-ivory">
            <div className="container-editorial py-20 md:py-28">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
                    {/* Brand block */}
                    <div className="md:col-span-5">
                        <p className="font-serif text-3xl md:text-4xl leading-tight">Chrissa Valanti</p>
                        <p className="font-serif italic text-xl md:text-2xl text-gold/90 mt-4">
                            Begin from where you are.
                        </p>
                        <p className="body-base text-ivory/60 mt-8 max-w-md">
                            Personal reflections, founder updates, music and behind-the-scenes stories — sent
                            occasionally, never noisily.
                        </p>

                        <form
                            data-testid={TID.footer.signup}
                            onSubmit={onSubmit}
                            className="mt-8 flex items-end gap-4 max-w-md"
                        >
                            <LiveRegion message={liveMessage} />
                            <div className="flex-1">
                                <label htmlFor="footer-email" className="eyebrow text-ivory/50">
                                    Email
                                </label>
                                <input
                                    id="footer-email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@somewhere.com"
                                    className="input-line-light mt-2"
                                    data-testid="footer-signup-email"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="footer-signup-submit"
                                className="pb-3 text-gold hover:text-ivory transition-colors disabled:opacity-40"
                                aria-label="Subscribe"
                            >
                                <Send size={20} strokeWidth={1.5} />
                            </button>
                        </form>
                    </div>

                    {/* Navigation column */}
                    <div className="md:col-span-3">
                        <p className="eyebrow text-gold/80 mb-6">Explore</p>
                        <ul className="space-y-3 font-light">
                            <li><Link to="/journey" className="hover:text-gold transition-colors">My Journey</Link></li>
                            <li><Link to="/building" className="hover:text-gold transition-colors">What I'm Building</Link></li>
                            {/* <li><Link to="/journal" className="hover:text-gold transition-colors">Journal</Link></li>  Temporarily hidden — Journal pages and articles remain intact for later restoration. */}
                            <li><Link to="/media" className="hover:text-gold transition-colors">Media & Speaking</Link></li>
                            <li><Link to="/contact" className="hover:text-gold transition-colors">Contact</Link></li>
                        </ul>
                    </div>

                    {/* Ventures */}
                    <div className="md:col-span-2">
                        <p className="eyebrow text-gold/80 mb-6">Ventures</p>
                        <ul className="space-y-3 font-light">
                            <li><Link to="/building/valanti-labs" className="hover:text-gold transition-colors">Valanti Labs</Link></li>
                            <li><Link to="/building/alati" className="hover:text-gold transition-colors">ALATI</Link></li>
                            <li><Link to="/building/music" className="hover:text-gold transition-colors">Music</Link></li>
                        </ul>
                    </div>

                    {/* Connect */}
                    <div className="md:col-span-2">
                        <p className="eyebrow text-gold/80 mb-6">Connect</p>
                        <ul className="space-y-3 font-light">
                            <li>
                                <a href="#" aria-label="Instagram" className="inline-flex items-center gap-2 hover:text-gold transition-colors">
                                    <Instagram size={16} strokeWidth={1.5} /> Instagram
                                </a>
                            </li>
                            <li>
                                <a href="#" aria-label="Music" className="inline-flex items-center gap-2 hover:text-gold transition-colors">
                                    <Music2 size={16} strokeWidth={1.5} /> Music
                                </a>
                            </li>
                            <li>
                                <Link to="/contact" className="inline-flex items-center gap-2 hover:text-gold transition-colors">
                                    <Mail size={16} strokeWidth={1.5} /> Enquiries
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-20 pt-10 border-t border-ivory/10 flex flex-col md:flex-row gap-6 md:items-end md:justify-between">
                    <p className="font-serif italic text-lg text-ivory/60 max-w-md">
                        Perhaps your story is not finished either.
                    </p>
                    <div className="flex flex-wrap gap-6 text-xs uppercase tracking-[0.18em] text-ivory/50">
                        <Link to="/privacy" className="hover:text-gold transition-colors">Privacy</Link>
                        <Link to="/terms" className="hover:text-gold transition-colors">Terms</Link>
                        <span>© {new Date().getFullYear()} Chrissa Valanti</span>
                    </div>
                </div>

                <p className="mt-8 text-[11px] text-ivory/40 max-w-3xl leading-relaxed">
                    Personal stories shared on this website are for general information and reflection only. They are
                    not medical advice. Please consult a qualified clinician for medical, treatment or recovery
                    decisions.
                </p>
            </div>
        </footer>
    );
}
